import {useEffect, useId, useRef} from "react";
import {css} from "@panda/css";

import {ChevronLeftIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {ResponsiveModal} from "@pg-design/modal-module";
import {OFFER_TEST_IDS, RP_RESERVATION_TEST_IDS} from "@pg-mono/test-ids";

import {ReservationStepsContent} from "../../../../reservation/components/ReservationStepsContent";
import {getReservationModalProps} from "../../../../reservation/utils/get_reservation_modal_props";
import {IUnlockChoiceConfirmation, IUnlockChoiceModalLayoutProps} from "../../../types/IUnlockChoiceModalLayoutProps";
import {IUnlockChoiceReservationView} from "../../../types/IUnlockChoiceReservationView";
import {isUnlockChoiceOfferOrReservation} from "../../../utils/is_unlock_choice_offer_or_reservation";
import {isUnlockChoiceReservationOption} from "../../../utils/is_unlock_choice_reservation_option";
import {UnlockChoiceModalFooter} from "./UnlockChoiceModalFooter";
import {UnlockChoiceModalOptionCard} from "./UnlockChoiceModalOptionCard";
import {UnlockChoiceModalPriceHeader} from "./UnlockChoiceModalPriceHeader";
import {UnlockChoiceModalPriceNegotiation} from "./UnlockChoiceModalPriceNegotiation";
import {UnlockChoiceModalPriceNegotiationFooter} from "./UnlockChoiceModalPriceNegotiationFooter";
import {UnlockChoiceModalTitle} from "./UnlockChoiceModalTitle";
import {UnlockChoiceSentContent} from "./UnlockChoiceSentContent";

// What the modal shows - each step after the choice takes the place of the cards
type UnlockChoiceModalView =
    | {kind: "reservation"; reservation: IUnlockChoiceReservationView}
    | {kind: "confirmation"; confirmation: IUnlockChoiceConfirmation}
    | {kind: "price_negotiation"}
    | {kind: "loading"}
    | {kind: "choice"};

const getView = (props: IUnlockChoiceModalLayoutProps): UnlockChoiceModalView => {
    if (props.reservation) {
        return {kind: "reservation", reservation: props.reservation};
    }

    if (props.confirmation) {
        return {kind: "confirmation", confirmation: props.confirmation};
    }

    if (props.isPriceNegotiationShown) {
        return {kind: "price_negotiation"};
    }

    return props.isLoading ? {kind: "loading"} : {kind: "choice"};
};

const VIEW_TEST_IDS: Record<Exclude<UnlockChoiceModalView["kind"], "reservation">, string> = {
    confirmation: OFFER_TEST_IDS.LEAD_FORM.UNLOCK_CHOICE_CONFIRMATION,
    price_negotiation: OFFER_TEST_IDS.LEAD_FORM.UNLOCK_CHOICE_PRICE_NEGOTIATION,
    loading: OFFER_TEST_IDS.LEAD_FORM.UNLOCK_CHOICE_LOADER,
    choice: OFFER_TEST_IDS.LEAD_FORM.UNLOCK_CHOICE_MODAL
};

export const UnlockChoiceModalLayout = (props: IUnlockChoiceModalLayoutProps) => {
    const {
        isOpen,
        isLoading,
        entryPoint,
        firstName,
        price,
        offerType,
        vendorName,
        privacyPolicyUrl,
        options,
        selectedOptionId,
        isSending,
        isConfirmDisabled,
        isPriceNegotiationArmed,
        onSelectOption,
        onConfirm,
        onClose,
        onAfterClose,
        zIndex
    } = props;

    const titleId = useId();
    const descriptionId = useId();
    const optionInputName = useId();
    const radioGroupRef = useRef<HTMLDivElement>(null);
    const wasLoadingRef = useRef(isLoading);
    const view = getView(props);
    const isReservation = view.kind === "reservation";
    const wasReservationRef = useRef(isReservation);
    // the confirmation and the price negotiation are described by their texts - the choice by its title only
    const isDescribedStep = view.kind === "confirmation" || view.kind === "price_negotiation";
    const isChoice = view.kind === "loading" || view.kind === "choice";

    useEffect(() => {
        const wasLoading = wasLoadingRef.current;
        wasLoadingRef.current = isLoading;

        const radioGroup = radioGroupRef.current;
        const dialog = radioGroup?.closest('[role="dialog"]');
        const activeElement = document.activeElement;

        if (!wasLoading || isLoading || !radioGroup || !dialog || (activeElement !== dialog && dialog.contains(activeElement))) {
            return;
        }

        radioGroup.querySelector<HTMLInputElement>("input:checked")?.focus({preventScroll: true});
    }, [isLoading]);

    // the reservation steps keep the focus inside the dialog on their own
    useEffect(() => {
        if (!isDescribedStep) {
            return;
        }

        const dialog = document.getElementById(titleId)?.closest<HTMLElement>('[role="dialog"]');

        if (dialog && !dialog.contains(document.activeElement)) {
            dialog.focus({preventScroll: true});
        }
    }, [view.kind]);

    useEffect(() => {
        const wasReservation = wasReservationRef.current;
        wasReservationRef.current = isReservation;

        // back from the code step the back button is gone - the focus returns to the selected option
        if (wasReservation && !isReservation) {
            radioGroupRef.current?.querySelector<HTMLInputElement>("input:checked")?.focus({preventScroll: true});
        }
    }, [isReservation]);

    const selectedOption = options.find((option) => option.id === selectedOptionId) ?? options[0];
    const headerPrice = !isLoading && isUnlockChoiceOfferOrReservation(entryPoint) ? price : null;

    const getModalProps = () => {
        if (view.kind === "reservation") {
            const {reservation} = view;

            return getReservationModalProps({step: reservation.step, isInitiating: reservation.codeStepProps.isInitiating, titleId, descriptionId});
        }

        const isLabelledByTitle = view.kind !== "loading";

        return {
            shouldCloseOnOverlayClick: view.kind === "price_negotiation" && isPriceNegotiationArmed,
            contentLabel: isLabelledByTitle ? undefined : "Wczytywanie opcji",
            ariaLabelledBy: isLabelledByTitle ? titleId : undefined,
            ariaDescribedBy: isDescribedStep ? descriptionId : undefined,
            dataTestId: VIEW_TEST_IDS[view.kind]
        };
    };

    const getHeaderClassName = () => {
        if (isLoading) {
            return loaderHeaderStyle;
        }

        return headerPrice ? priceHeaderStyle : titleHeaderStyle;
    };

    const getBodyClassName = () => {
        switch (view.kind) {
            case "loading":
                return loaderBodyStyle;
            case "choice":
                return headerPrice ? priceBodyStyle : titleBodyStyle;
            default:
                // the steps after the choice bring their own paddings
                return undefined;
        }
    };

    const renderHeaderContent = () => {
        if (isLoading) {
            return null;
        }

        if (headerPrice) {
            return <UnlockChoiceModalPriceHeader price={headerPrice} offerType={offerType} />;
        }

        return <UnlockChoiceModalTitle id={titleId} firstName={firstName} variant="header" className={headerTitleStyle} />;
    };

    const renderBodyContent = () => {
        switch (view.kind) {
            case "reservation":
                return (
                    <ReservationStepsContent
                        step={view.reservation.step}
                        titleId={titleId}
                        descriptionId={descriptionId}
                        codeStepProps={view.reservation.codeStepProps}
                        confirmedStepProps={view.reservation.confirmedStepProps}
                        onClose={onClose}
                        onConfirmedClose={view.reservation.onConfirmedClose}
                    />
                );
            case "confirmation":
                return (
                    <UnlockChoiceSentContent
                        titleId={titleId}
                        descriptionId={descriptionId}
                        vendorName={view.confirmation.vendorName}
                        subject={view.confirmation.subject}
                    />
                );
            case "price_negotiation":
                return <UnlockChoiceModalPriceNegotiation titleId={titleId} descriptionId={descriptionId} offerType={offerType} />;
            case "loading":
                return <Loader size="lg" fullWidth />;
            case "choice":
                return (
                    <>
                        {headerPrice && <UnlockChoiceModalTitle id={titleId} firstName={firstName} variant="question" />}

                        <div ref={radioGroupRef} role="radiogroup" aria-labelledby={titleId} className={headerPrice ? priceOptionsStyle : titleOptionsStyle}>
                            {options.map((option) => (
                                <UnlockChoiceModalOptionCard
                                    key={option.id}
                                    option={option}
                                    inputName={optionInputName}
                                    isSelected={option.id === selectedOption.id}
                                    isDisabled={isSending}
                                    onSelect={onSelectOption}
                                    dataTestId={isUnlockChoiceReservationOption(option) ? RP_RESERVATION_TEST_IDS.UNLOCK_CHOICE_RESERVATION_OPTION : undefined}
                                />
                            ))}
                        </div>
                    </>
                );
        }
    };

    return (
        <ResponsiveModal isOpen={isOpen} onClose={onClose} onAfterClose={onAfterClose} zIndex={zIndex} {...getModalProps()}>
            {isChoice && (
                <ResponsiveModal.Header divider={headerPrice !== null} className={getHeaderClassName()}>
                    {renderHeaderContent()}
                    <ResponsiveModal.CloseButton
                        className={headerPrice ? priceHeaderCloseButtonStyle : undefined}
                        dataTestId={OFFER_TEST_IDS.LEAD_FORM.UNLOCK_CHOICE_CLOSE_BUTTON}
                    />
                </ResponsiveModal.Header>
            )}

            {view.kind === "reservation" && view.reservation.step === "code" && (
                <ResponsiveModal.Header divider={false} className={reservationHeaderStyle}>
                    <button
                        type="button"
                        aria-label="Wróć"
                        className={backButtonStyle}
                        disabled={view.reservation.isSubmitting}
                        onClick={view.reservation.onBack}
                        data-testid={RP_RESERVATION_TEST_IDS.BACK_BUTTON}
                    >
                        <ChevronLeftIcon size="1.6" fill="var(--colors-secondary)" />
                    </button>
                    <ResponsiveModal.CloseButton dataTestId={RP_RESERVATION_TEST_IDS.CLOSE_BUTTON} />
                </ResponsiveModal.Header>
            )}

            <ResponsiveModal.Body className={getBodyClassName()}>{renderBodyContent()}</ResponsiveModal.Body>

            {view.kind === "price_negotiation" && (
                <UnlockChoiceModalPriceNegotiationFooter
                    vendorName={vendorName}
                    privacyPolicyUrl={privacyPolicyUrl}
                    isSending={isSending}
                    isConfirmDisabled={isConfirmDisabled}
                    isArmed={isPriceNegotiationArmed}
                    onConfirm={onConfirm}
                />
            )}

            {view.kind === "choice" && (
                <UnlockChoiceModalFooter
                    vendorName={vendorName}
                    privacyPolicyUrl={privacyPolicyUrl}
                    isClauseVisible={selectedOption.sendsLead || isUnlockChoiceReservationOption(selectedOption)}
                    ctaText={selectedOption.ctaText}
                    isSending={isSending}
                    isConfirmDisabled={isConfirmDisabled}
                    onConfirm={onConfirm}
                />
            )}
        </ResponsiveModal>
    );
};

const reservationHeaderStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    px: 2,
    pt: 1.5,
    md: {
        px: 3
    }
});

const backButtonStyle = css({
    display: "inline-flex",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    width: "3.2rem",
    height: "3.2rem",
    p: 0,
    border: "none",
    borderRadius: "0.8rem",
    backgroundColor: "transparent",
    cursor: "pointer",
    _focusVisible: {
        outline: "2px solid var(--colors-secondary)",
        outlineOffset: "2px"
    },
    _disabled: {
        cursor: "default",
        opacity: 0.4
    }
});

const loaderHeaderStyle = css({
    display: "flex",
    justifyContent: "flex-end",
    px: 2,
    py: 1.5,
    md: {
        px: 3
    }
});

const priceHeaderStyle = css({
    position: "relative",
    pt: 1.5,
    pb: 3,
    pl: 2,
    pr: 6,
    md: {
        pb: 1.5,
        pl: 3,
        pr: 7
    }
});

const priceHeaderCloseButtonStyle = css({
    position: "absolute",
    top: 1.5,
    right: 2,
    md: {
        right: 3
    }
});

const titleHeaderStyle = css({
    display: "flex",
    alignItems: "flex-start",
    columnGap: "1rem",
    px: 2,
    py: 1.5,
    md: {
        px: 3
    }
});

const headerTitleStyle = css({
    flex: "1 1 auto",
    minWidth: 0
});

const priceBodyStyle = css({
    px: 2,
    pt: 2,
    pb: 1,
    md: {
        px: 3,
        pt: 3.5,
        pb: 2
    }
});

const titleBodyStyle = css({
    px: 2,
    pt: 1,
    pb: 1,
    md: {
        px: 3,
        pt: 3,
        pb: 2
    }
});

const priceOptionsStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1.5,
    pt: 1.5,
    md: {
        rowGap: 2,
        pt: 3.5
    }
});

const titleOptionsStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1.5,
    md: {
        rowGap: 2
    }
});

const loaderBodyStyle = css({
    px: 2,
    pb: 4,
    md: {
        px: 3
    }
});
import {useEffect, useId, useRef} from "react";
import {css} from "@panda/css";

import {ChevronLeftIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {ResponsiveModal} from "@pg-design/modal-module";
import {OFFER_TEST_IDS, RP_RESERVATION_TEST_IDS} from "@pg-mono/test-ids";

import {ReservationStepsContent} from "../../../../reservation/components/ReservationStepsContent";
import {getReservationModalProps} from "../../../../reservation/utils/get_reservation_modal_props";
import {IUnlockChoiceConfirmation, IUnlockChoiceModalLayoutProps} from "../../../types/IUnlockChoiceModalLayoutProps";
import {IUnlockChoiceReservationView} from "../../../types/IUnlockChoiceReservationView";
import {isUnlockChoiceOfferOrReservation} from "../../../utils/is_unlock_choice_offer_or_reservation";
import {isUnlockChoiceReservationOption} from "../../../utils/is_unlock_choice_reservation_option";
import {UnlockChoiceModalFooter} from "./UnlockChoiceModalFooter";
import {UnlockChoiceModalOptionCard} from "./UnlockChoiceModalOptionCard";
import {UnlockChoiceModalPriceHeader} from "./UnlockChoiceModalPriceHeader";
import {UnlockChoiceModalPriceNegotiation} from "./UnlockChoiceModalPriceNegotiation";
import {UnlockChoiceModalPriceNegotiationFooter} from "./UnlockChoiceModalPriceNegotiationFooter";
import {UnlockChoiceModalTitle} from "./UnlockChoiceModalTitle";
import {UnlockChoiceSentContent} from "./UnlockChoiceSentContent";

// What the modal shows - each step after the choice takes the place of the cards
type UnlockChoiceModalView =
    | {kind: "reservation"; reservation: IUnlockChoiceReservationView}
    | {kind: "confirmation"; confirmation: IUnlockChoiceConfirmation}
    | {kind: "price_negotiation"}
    | {kind: "loading"}
    | {kind: "choice"};

const getView = (props: IUnlockChoiceModalLayoutProps): UnlockChoiceModalView => {
    if (props.reservation) {
        return {kind: "reservation", reservation: props.reservation};
    }

    if (props.confirmation) {
        return {kind: "confirmation", confirmation: props.confirmation};
    }

    if (props.isPriceNegotiationShown) {
        return {kind: "price_negotiation"};
    }

    return props.isLoading ? {kind: "loading"} : {kind: "choice"};
};

const VIEW_TEST_IDS: Record<Exclude<UnlockChoiceModalView["kind"], "reservation">, string> = {
    confirmation: OFFER_TEST_IDS.LEAD_FORM.UNLOCK_CHOICE_CONFIRMATION,
    price_negotiation: OFFER_TEST_IDS.LEAD_FORM.UNLOCK_CHOICE_PRICE_NEGOTIATION,
    loading: OFFER_TEST_IDS.LEAD_FORM.UNLOCK_CHOICE_LOADER,
    choice: OFFER_TEST_IDS.LEAD_FORM.UNLOCK_CHOICE_MODAL
};

export const UnlockChoiceModalLayout = (props: IUnlockChoiceModalLayoutProps) => {
    const {
        isOpen,
        isLoading,
        entryPoint,
        firstName,
        price,
        offerType,
        vendorName,
        privacyPolicyUrl,
        options,
        selectedOptionId,
        isSending,
        isConfirmDisabled,
        isPriceNegotiationArmed,
        onSelectOption,
        onConfirm,
        onClose,
        onAfterClose,
        zIndex
    } = props;

    const titleId = useId();
    const descriptionId = useId();
    const optionInputName = useId();
    const radioGroupRef = useRef<HTMLDivElement>(null);
    const wasLoadingRef = useRef(isLoading);
    const view = getView(props);
    const isReservation = view.kind === "reservation";
    const wasReservationRef = useRef(isReservation);
    // the confirmation and the price negotiation are described by their texts - the choice by its title only
    const isDescribedStep = view.kind === "confirmation" || view.kind === "price_negotiation";
    const isChoice = view.kind === "loading" || view.kind === "choice";

    useEffect(() => {
        const wasLoading = wasLoadingRef.current;
        wasLoadingRef.current = isLoading;

        const radioGroup = radioGroupRef.current;
        const dialog = radioGroup?.closest('[role="dialog"]');
        const activeElement = document.activeElement;

        if (!wasLoading || isLoading || !radioGroup || !dialog || (activeElement !== dialog && dialog.contains(activeElement))) {
            return;
        }

        radioGroup.querySelector<HTMLInputElement>("input:checked")?.focus({preventScroll: true});
    }, [isLoading]);

    // the reservation steps keep the focus inside the dialog on their own
    useEffect(() => {
        if (!isDescribedStep) {
            return;
        }

        const dialog = document.getElementById(titleId)?.closest<HTMLElement>('[role="dialog"]');

        if (dialog && !dialog.contains(document.activeElement)) {
            dialog.focus({preventScroll: true});
        }
    }, [view.kind]);

    useEffect(() => {
        const wasReservation = wasReservationRef.current;
        wasReservationRef.current = isReservation;

        // back from the code step the back button is gone - the focus returns to the selected option
        if (wasReservation && !isReservation) {
            radioGroupRef.current?.querySelector<HTMLInputElement>("input:checked")?.focus({preventScroll: true});
        }
    }, [isReservation]);

    const selectedOption = options.find((option) => option.id === selectedOptionId) ?? options[0];
    const headerPrice = !isLoading && isUnlockChoiceOfferOrReservation(entryPoint) ? price : null;

    const getModalProps = () => {
        if (view.kind === "reservation") {
            const {reservation} = view;

            return getReservationModalProps({step: reservation.step, isInitiating: reservation.codeStepProps.isInitiating, titleId, descriptionId});
        }

        const isLabelledByTitle = view.kind !== "loading";

        return {
            shouldCloseOnOverlayClick: view.kind === "price_negotiation" && isPriceNegotiationArmed,
            contentLabel: isLabelledByTitle ? undefined : "Wczytywanie opcji",
            ariaLabelledBy: isLabelledByTitle ? titleId : undefined,
            ariaDescribedBy: isDescribedStep ? descriptionId : undefined,
            dataTestId: VIEW_TEST_IDS[view.kind]
        };
    };

    const getHeaderClassName = () => {
        if (isLoading) {
            return loaderHeaderStyle;
        }

        return headerPrice ? priceHeaderStyle : titleHeaderStyle;
    };

    const getBodyClassName = () => {
        switch (view.kind) {
            case "loading":
                return loaderBodyStyle;
            case "choice":
                return headerPrice ? priceBodyStyle : titleBodyStyle;
            default:
                // the steps after the choice bring their own paddings
                return undefined;
        }
    };

    const renderHeaderContent = () => {
        if (isLoading) {
            return null;
        }

        if (headerPrice) {
            return <UnlockChoiceModalPriceHeader price={headerPrice} offerType={offerType} />;
        }

        return <UnlockChoiceModalTitle id={titleId} firstName={firstName} variant="header" className={headerTitleStyle} />;
    };

    const renderBodyContent = () => {
        switch (view.kind) {
            case "reservation":
                return (
                    <ReservationStepsContent
                        step={view.reservation.step}
                        titleId={titleId}
                        descriptionId={descriptionId}
                        codeStepProps={view.reservation.codeStepProps}
                        confirmedStepProps={view.reservation.confirmedStepProps}
                        onClose={onClose}
                        onConfirmedClose={view.reservation.onConfirmedClose}
                    />
                );
            case "confirmation":
                return (
                    <UnlockChoiceSentContent
                        titleId={titleId}
                        descriptionId={descriptionId}
                        vendorName={view.confirmation.vendorName}
                        subject={view.confirmation.subject}
                    />
                );
            case "price_negotiation":
                return <UnlockChoiceModalPriceNegotiation titleId={titleId} descriptionId={descriptionId} offerType={offerType} />;
            case "loading":
                return <Loader size="lg" fullWidth />;
            case "choice":
                return (
                    <>
                        {headerPrice && <UnlockChoiceModalTitle id={titleId} firstName={firstName} variant="question" />}

                        <div ref={radioGroupRef} role="radiogroup" aria-labelledby={titleId} className={headerPrice ? priceOptionsStyle : titleOptionsStyle}>
                            {options.map((option) => (
                                <UnlockChoiceModalOptionCard
                                    key={option.id}
                                    option={option}
                                    inputName={optionInputName}
                                    isSelected={option.id === selectedOption.id}
                                    isDisabled={isSending}
                                    onSelect={onSelectOption}
                                    dataTestId={isUnlockChoiceReservationOption(option) ? RP_RESERVATION_TEST_IDS.UNLOCK_CHOICE_RESERVATION_OPTION : undefined}
                                />
                            ))}
                        </div>
                    </>
                );
        }
    };

    return (
        <ResponsiveModal isOpen={isOpen} onClose={onClose} onAfterClose={onAfterClose} zIndex={zIndex} {...getModalProps()}>
            {isChoice && (
                <ResponsiveModal.Header divider={headerPrice !== null} className={getHeaderClassName()}>
                    {renderHeaderContent()}
                    <ResponsiveModal.CloseButton
                        className={headerPrice ? priceHeaderCloseButtonStyle : undefined}
                        dataTestId={OFFER_TEST_IDS.LEAD_FORM.UNLOCK_CHOICE_CLOSE_BUTTON}
                    />
                </ResponsiveModal.Header>
            )}

            {view.kind === "reservation" && view.reservation.step === "code" && (
                <ResponsiveModal.Header divider={false} className={reservationHeaderStyle}>
                    <button
                        type="button"
                        aria-label="Wróć"
                        className={backButtonStyle}
                        disabled={view.reservation.isSubmitting}
                        onClick={view.reservation.onBack}
                        data-testid={RP_RESERVATION_TEST_IDS.BACK_BUTTON}
                    >
                        <ChevronLeftIcon size="1.6" fill="var(--colors-secondary)" />
                    </button>
                    <ResponsiveModal.CloseButton dataTestId={RP_RESERVATION_TEST_IDS.CLOSE_BUTTON} />
                </ResponsiveModal.Header>
            )}

            <ResponsiveModal.Body className={getBodyClassName()}>{renderBodyContent()}</ResponsiveModal.Body>

            {view.kind === "price_negotiation" && (
                <UnlockChoiceModalPriceNegotiationFooter
                    vendorName={vendorName}
                    privacyPolicyUrl={privacyPolicyUrl}
                    isSending={isSending}
                    isConfirmDisabled={isConfirmDisabled}
                    isArmed={isPriceNegotiationArmed}
                    onConfirm={onConfirm}
                />
            )}

            {view.kind === "choice" && (
                <UnlockChoiceModalFooter
                    vendorName={vendorName}
                    privacyPolicyUrl={privacyPolicyUrl}
                    isClauseVisible={selectedOption.sendsLead || isUnlockChoiceReservationOption(selectedOption)}
                    ctaText={selectedOption.ctaText}
                    isSending={isSending}
                    isConfirmDisabled={isConfirmDisabled}
                    onConfirm={onConfirm}
                />
            )}
        </ResponsiveModal>
    );
};

const reservationHeaderStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    px: 2,
    pt: 1.5,
    md: {
        px: 3
    }
});

const backButtonStyle = css({
    display: "inline-flex",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    width: "3.2rem",
    height: "3.2rem",
    p: 0,
    border: "none",
    borderRadius: "0.8rem",
    backgroundColor: "transparent",
    cursor: "pointer",
    _focusVisible: {
        outline: "2px solid var(--colors-secondary)",
        outlineOffset: "2px"
    },
    _disabled: {
        cursor: "default",
        opacity: 0.4
    }
});

const loaderHeaderStyle = css({
    display: "flex",
    justifyContent: "flex-end",
    px: 2,
    py: 1.5,
    md: {
        px: 3
    }
});

const priceHeaderStyle = css({
    position: "relative",
    pt: 1.5,
    pb: 3,
    pl: 2,
    pr: 6,
    md: {
        pb: 1.5,
        pl: 3,
        pr: 7
    }
});

const priceHeaderCloseButtonStyle = css({
    position: "absolute",
    top: 1.5,
    right: 2,
    md: {
        right: 3
    }
});

const titleHeaderStyle = css({
    display: "flex",
    alignItems: "flex-start",
    columnGap: "1rem",
    px: 2,
    py: 1.5,
    md: {
        px: 3
    }
});

const headerTitleStyle = css({
    flex: "1 1 auto",
    minWidth: 0
});

const priceBodyStyle = css({
    px: 2,
    pt: 2,
    pb: 1,
    md: {
        px: 3,
        pt: 3.5,
        pb: 2
    }
});

const titleBodyStyle = css({
    px: 2,
    pt: 1,
    pb: 1,
    md: {
        px: 3,
        pt: 3,
        pb: 2
    }
});

const priceOptionsStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1.5,
    pt: 1.5,
    md: {
        rowGap: 2,
        pt: 3.5
    }
});

const titleOptionsStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1.5,
    md: {
        rowGap: 2
    }
});

const loaderBodyStyle = css({
    px: 2,
    pb: 4,
    md: {
        px: 3
    }
});
